import { Component, useState, type ErrorInfo, type ReactNode } from 'react';
import {
  isStaleBuildAssetError,
  reloadApplicationForLatestBuild,
  requestStaleBuildRecovery,
} from './stale-build-recovery.ts';
import { createDeveloperLog } from '#kernel/feedback/developer-log.ts';

// This boundary is part of the entry, so it cannot use the shared
// CopyDevLogsButton: its tooltip and toast would put Ant Design in front of
// every first paint. A native button also still works when the failure is a
// missing chunk.
function CopyDevLogsButton({ error }: { error: unknown }) {
  const [copyResult, setCopyResult] = useState<'copied' | 'failed' | null>(null);
  return (
    <button
      type="button"
      className="copy-dev-logs"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(
            createDeveloperLog(error, [{ label: 'Surface', value: 'application-load-recovery' }]),
          );
          setCopyResult('copied');
        } catch {
          setCopyResult('failed');
        }
      }}
    >
      {copyResult === 'copied'
        ? 'Dev logs copied'
        : copyResult === 'failed'
          ? 'Could not copy. Try again'
          : 'Copy dev logs'}
    </button>
  );
}

interface ApplicationLoadRecoveryBoundaryProps {
  children: ReactNode;
}

interface ApplicationLoadRecoveryBoundaryState {
  error: unknown | null;
  recoveryDecision: 'pending' | 'started' | 'unavailable';
}

export default class ApplicationLoadRecoveryBoundary extends Component<
  ApplicationLoadRecoveryBoundaryProps,
  ApplicationLoadRecoveryBoundaryState
> {
  override state: ApplicationLoadRecoveryBoundaryState = {
    error: null,
    recoveryDecision: 'pending',
  };

  static getDerivedStateFromError(error: unknown): ApplicationLoadRecoveryBoundaryState {
    return { error, recoveryDecision: 'pending' };
  }

  override componentDidCatch(error: unknown, _errorInfo: ErrorInfo): void {
    this.setState({
      recoveryDecision: requestStaleBuildRecovery(error) ? 'started' : 'unavailable',
    });
  }

  override render() {
    const { children } = this.props;
    const { error, recoveryDecision } = this.state;
    if (!error) return children;

    const refreshing = isStaleBuildAssetError(error) && recoveryDecision !== 'unavailable';
    const title = refreshing ? 'Refreshing application' : 'Application could not load';

    return (
      <main
        aria-labelledby="application-load-recovery-title"
        aria-live="polite"
        className="application-load-recovery"
        role={refreshing ? 'status' : 'alert'}
      >
        <section className="application-load-recovery__panel">
          <h1 id="application-load-recovery-title">{title}</h1>
          <p>
            {refreshing
              ? 'A current application version is loading. Your page will be preserved.'
              : 'Reload the application to continue. Your page will be preserved.'}
          </p>
          {!refreshing && (
            <>
              <button
                aria-keyshortcuts="Enter"
                autoFocus
                type="button"
                onClick={() => reloadApplicationForLatestBuild()}
              >
                Reload application
              </button>
              <CopyDevLogsButton error={error} />
            </>
          )}
        </section>
      </main>
    );
  }
}
